import React, { useState, useEffect, useRef } from 'react'
import copy from 'copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import QRCode from 'qrcode.react'

import { isBlockedMeta } from 'utils/common'

import { isEducation } from 'common/utils/permissions'
import Toast, { dismiss } from 'components/Toast/Toast'
import Mixpanel from 'common/services/Mixpanel'
import { isEuVersion } from 'common/constants/env'

import { API__PROJECTS } from 'api'

import useModalManager from 'hooks/useModalManager'

import { MODALS } from 'components/ModalManager/constants'
import CircleSpinner from 'components/CircleSpinner/CircleSpinner'

import { SHARE_MODAL_TABS, SOCIAL_BLOCKS } from '../../utils/constants'

import shareIcon from './i/share.svg'
import printIcon from './i/print.svg'
import embedIcon from './i/embed.svg'
import qrIcon from './i/qr.svg'
import communityIcon from './i/community.svg'
import embedManIcon from './i/embedMan.svg'
import ResultflyIcon from './i/resultfly.png'

import ShareUrlBody from './components/ShareUrlBody/ShareUrlBody'
import PrintBody from './components/PrintBody/PrintBody'
import PrintProcessingBody from './components/PrintProcessingBody/PrintProcessingBody'
import WideBox from './components/WideBox/WideBox'
import CompactBox from './components/CompactBox/CompactBox'
import SocialMedia from './components/SocialMedia/SocialMedia'
import ResultFlyBox from './components/ResultFlyBox/ResultFlyBox'

import styles from './Main.module.scss'

const Main = ({ projectAuthor, projectUrl, project, setTab, onOpenCreateTemplateModal }) => {
    const { openModal } = useModalManager()
    const generatePdfToastId = useRef(null)
    const { t } = useTranslation()
    const user_details = useSelector(state => state.user_details)
    const [isCanCreateTemplate, setIsCanCreateTemplate] = useState(false)
    const [isPdfLoading, setIsPdfLoading] = useState(false)
    const [compactCardsWidth, setCompactCardsWidth] = useState('0px')

    useEffect(() => {
        if (isCanCreateTemplate) setCompactCardsWidth('calc(33.3% - 8px)')
        else setCompactCardsWidth('calc(50% - 8px)')
    }, [isCanCreateTemplate])

    useEffect(() => {
        if (!isEducation() || isEuVersion) {
            setIsCanCreateTemplate(false)
            return
        }
        setIsCanCreateTemplate(user_details.id === projectAuthor.userId)
    }, [user_details.id, projectAuthor.userId, isEuVersion])

    if (isBlockedMeta()) delete SOCIAL_BLOCKS.facebook

    const handleCopyProjectUrl = () => {
        copy(projectUrl)
        Mixpanel.track('Project url copied', { url: projectUrl })
        Toast('success', {
            title: t('Link copied!'),
            time: 2000,
        })
    }

    const handleGetProjectPdf = async () => {
        try {
            const fileName = `${project.name}.pdf`
            generatePdfToastId.current = Toast('info', {
                title: t('Generating PDF'),
                message: fileName,
                IconComponent: <CircleSpinner />,
                time: 999999,
            })
            setIsPdfLoading(true)

            const url = await API__PROJECTS.GET_PROJECT_PDF(project.projectId, project.name)

            openModal(MODALS.PDF_GENERATION, { url, fileName })
        } catch (err) {
            console.error(err)
            Toast('error', {
                title: t('Error when creating a PDF file of the project'),
                time: 2000,
            })
        } finally {
            setIsPdfLoading(false)
            dismiss(generatePdfToastId.current)
        }
    }
    const isEdu = isEducation()

    return (
        <div className={styles.main}>
            <div className={styles.wideCards}>
                <WideBox
                    title={isEdu ? t('Share with students') : t('Direct URL to your Project')}
                    image={shareIcon}
                    body={<ShareUrlBody projectUrl={projectUrl} onCopyProjectUrl={handleCopyProjectUrl} />}
                />
            </div>
            {isEdu && (
                <WideBox
                    title={t('Convert to PDF and print')}
                    isNewLabel
                    image={printIcon}
                    body={
                        isPdfLoading ? (
                            <PrintProcessingBody />
                        ) : (
                            <PrintBody projectUrl={projectUrl} onGetProjectPdf={handleGetProjectPdf} />
                        )
                    }
                />
            )}

            <div className={styles.compactCards}>
                {isEdu ? (
                    <CompactBox
                        width={compactCardsWidth}
                        title={t('Embed in LMS')}
                        icon={embedIcon}
                        text={t('Embed in your website or school LMS')}
                        onClick={() => setTab(SHARE_MODAL_TABS.embedCode)}
                    />
                ) : (
                    <CompactBox
                        width={compactCardsWidth}
                        title={t('Embed in a website')}
                        icon={embedIcon}
                        additionalBlock={<img src={embedManIcon} alt="" />}
                        text={t('Get iframe embed code options')}
                        onClick={() => setTab(SHARE_MODAL_TABS.embedCode)}
                    />
                )}
                <CompactBox
                    width={compactCardsWidth}
                    title={t('Get QR code')}
                    icon={qrIcon}
                    additionalBlock={
                        <QRCode
                            value={projectUrl}
                            size={isEdu ? 60 : 100}
                            level="M"
                            bgColor="#FFFFFF"
                            fgColor="#000000"
                            includeMargin={false}
                            renderAs="canvas"
                        />
                    }
                    text={t('For a quick start on the phone')}
                    onClick={() => setTab(SHARE_MODAL_TABS.qrCode)}
                />
                {isCanCreateTemplate && (
                    <CompactBox
                        width={compactCardsWidth}
                        title={t('Share with the community')}
                        icon={communityIcon}
                        readMoreLink="https://help.interacty.me/en/content/how-to-create-a-public-template-based-on-your-project"
                        onClick={onOpenCreateTemplateModal}
                    />
                )}
            </div>
            {!isEdu && (
                <ResultFlyBox
                    title={t('Scale this into a full campaign')}
                    image={ResultflyIcon}
                    body={t('Complex workflows, progress tracking, emails, and more')}
                    isNewLabel
                    buttonText={t('Learn more')}
                    buttonHref="https://interacty.me/pricing/business?group=enterprise&utm_source=interacty&utm_medium=banner&utm_campaign=publish_screen"
                />
            )}

            <SocialMedia projectUrl={projectUrl} />
        </div>
    )
}

export default Main
